/**
 * API Configuration
 * Central configuration for all external API endpoints
 */

// Base URL for all API endpoints - configurable via environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  'https://s54yjn65k4.execute-api.us-east-1.amazonaws.com/v1';

// Type definitions
export interface ApiEndpoint {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
}

// Base API response interface
export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  message?: string;
}

export interface ApiResult<T> {
  data: T;
  status: number;
  headers: Headers;
}

// Transaction-specific types based on OpenAPI spec
export interface TransactionItem {
  PK: string;
  SK: string;
  amount: number;
  type: 'debit' | 'credit' | 'savings';
  description: string;
  category: string;
  goal?: string; // Optional: ID of the savings goal for 'savings' type transactions
  createdAt: string;
  updatedAt: string;
}

export interface GetTransactionsResponse {
  transactions: TransactionItem[];
  count: number;
  month: string;
}

export interface CreateTransactionResponse {
  message: string;
  transaction: TransactionItem;
}

export interface UpdateTransactionRequest {
  amount?: number;
  type?: 'debit' | 'credit' | 'savings';
  description?: string;
  category?: string;
  goal?: string;
}

export interface UpdateTransactionResponse {
  message: string;
  transaction: TransactionItem;
}

export interface DeleteTransactionResponse {
  message: string;
  transactionId: string;
}

// Budget-specific types based on OpenAPI spec
export interface BudgetItemInput {
  name: string;
  amount: number;
  kind: 'income' | 'expense';
  currency: string;
}

export interface BudgetCategoryItem {
  PK: string;
  SK: string;
  amount: number;
  kind: 'income' | 'expense';
  currency: string;
}

export interface BudgetSummary {
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
  count?: number;
}

export interface StoreBudgetRequest {
  items: BudgetItemInput[];
}

export interface StoreBudgetResponse {
  message: string;
  items: BudgetCategoryItem[];
  summary: BudgetSummary;
}

export interface GetBudgetResponse {
  items: BudgetCategoryItem[];
  summary: BudgetSummary;
}

export interface DeleteBudgetCategoryResponse {
  message: string;
  categoryName: string;
}

// Transaction categorization types
export interface TransactionCategorizationResponse {
  merchant: string;
  category: string;
  confidence: number;
}

// Transaction parsing types based on OpenAPI spec
export interface ParsedTransaction {
  amount: number;
  type: 'debit' | 'credit' | 'savings';
  description: string;
  category: string;
  date: string; // YYYY-MM-DD format
}

export interface TransactionParseRequest {
  description: string;
  referenceDate?: string; // Optional ISO 8601 formatted reference date
}

export interface TransactionParseResponse {
  transactions: ParsedTransaction[];
}

// Financial parser types
export interface FinancialItem {
  description: string;
  amount: number;
}

export interface FinancialParseResponse {
  income: FinancialItem[];
  expenses: FinancialItem[];
}

// Error response type
export interface ErrorResponse {
  error: string;
  details?: string[];
}

// Spending limits types based on OpenAPI spec
export interface SetSpendingLimitRequest {
  category: string;
  limit: number;
}

export interface SpendingLimitItem {
  PK: string;
  SK: string;
  category: string;
  limit: number;
  createdAt: string;
  updatedAt: string;
}

export interface SetSpendingLimitResponse {
  message: string;
  limit: SpendingLimitItem;
}

export interface GetSpendingLimitsResponse {
  limits: SpendingLimitItem[];
  count: number;
}

export interface GetCategorySpendingLimitResponse {
  limit: SpendingLimitItem;
}

export interface DeleteSpendingLimitResponse {
  message: string;
  category: string;
}

// Savings goals types based on OpenAPI spec
export interface CreateSavingsGoalRequest {
  name: string;
  targetAmount: number;
  duration: number;
}

export interface UpdateSavingsGoalRequest {
  name?: string;
  targetAmount?: number;
  duration?: number;
  status?: 'active' | 'completed' | 'paused';
  actualProgress?: number;
  pausedDate?: string;
  resumedDate?: string;
  totalPausedDays?: number;
}

export interface SavingsGoalRecord {
  PK: string;
  SK: string;
  name: string;
  targetAmount: number;
  duration: number;
  createdDate: string;
  status: 'active' | 'completed' | 'paused';
  updatedAt: string;
  pausedDate?: string;
  resumedDate?: string;
  totalPausedDays: number;
  actualProgress: number;
}

export interface CreateSavingsGoalResponse {
  message: string;
  goal: SavingsGoalRecord;
}

export interface GetSavingsGoalsResponse {
  goals: SavingsGoalRecord[];
  count: number;
}

export interface UpdateSavingsGoalResponse {
  message: string;
  goal: SavingsGoalRecord;
}

export interface DeleteSavingsGoalResponse {
  message: string;
  goalId: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public endpoint?: string,
    public method?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const API_ENDPOINTS = {
  // AI Lambda function for transaction categorization
  TRANSACTION_CATEGORIZATION: {
    url: `${API_BASE_URL}/categorize`,
    method: 'POST'
  } as ApiEndpoint,
  
  // AI Lambda function for natural language budget processing
  BUDGET_PROCESSING: {
    url: `${API_BASE_URL}/financial/parse`,
    method: 'POST'
  } as ApiEndpoint,
  
  // Transaction management endpoints
  TRANSACTIONS: {
    CREATE: {
      url: `${API_BASE_URL}/transactions`,
      method: 'POST'
    } as ApiEndpoint,
    GET: {
      url: `${API_BASE_URL}/transactions`,
      method: 'GET'
    } as ApiEndpoint,
    UPDATE: (transactionId: string) => ({
      url: `${API_BASE_URL}/transactions/${encodeURIComponent(transactionId)}`,
      method: 'PUT'
    } as ApiEndpoint),
    DELETE: {
      url: `${API_BASE_URL}/transactions`,
      method: 'DELETE'
    } as ApiEndpoint,
    PARSE: {
      url: `${API_BASE_URL}/transactions/parse`,
      method: 'POST'
    } as ApiEndpoint
  },

  // Budget management endpoints
  BUDGET: {
    STORE: {
      url: `${API_BASE_URL}/budget`,
      method: 'POST'
    } as ApiEndpoint,
    GET: {
      url: `${API_BASE_URL}/budget`,
      method: 'GET'
    } as ApiEndpoint,
    DELETE_CATEGORY: {
      url: `${API_BASE_URL}/budget`,
      method: 'DELETE'
    } as ApiEndpoint
  },

  // User configuration endpoints
  CONFIG: {
    UPDATE: {
      url: `${API_BASE_URL}/config`,
      method: 'PATCH'
    } as ApiEndpoint,
    GET: {
      url: `${API_BASE_URL}/config`,
      method: 'GET'
    } as ApiEndpoint
  },

  // Spending limits endpoints
  SPENDING_LIMITS: {
    GET_ALL: {
      url: `${API_BASE_URL}/spending-limits`,
      method: 'GET'
    } as ApiEndpoint,
    SET_LIMIT: {
      url: `${API_BASE_URL}/spending-limits`,
      method: 'POST'
    } as ApiEndpoint,
    GET_CATEGORY: (category: string) => ({
      url: `${API_BASE_URL}/spending-limits/${encodeURIComponent(category)}`,
      method: 'GET'
    } as ApiEndpoint),
    DELETE_CATEGORY: (category: string) => ({
      url: `${API_BASE_URL}/spending-limits/${encodeURIComponent(category)}`,
      method: 'DELETE'
    } as ApiEndpoint)
  },

  // Savings goals endpoints
  SAVINGS_GOALS: {
    CREATE: {
      url: `${API_BASE_URL}/savings-goals`,
      method: 'POST'
    } as ApiEndpoint,
    GET_ALL: {
      url: `${API_BASE_URL}/savings-goals`,
      method: 'GET'
    } as ApiEndpoint,
    UPDATE: (goalId: string) => ({
      url: `${API_BASE_URL}/savings-goals/${encodeURIComponent(goalId)}`,
      method: 'PUT'
    } as ApiEndpoint),
    DELETE: (goalId: string) => ({
      url: `${API_BASE_URL}/savings-goals/${encodeURIComponent(goalId)}`,
      method: 'DELETE'
    } as ApiEndpoint),
    PAUSE: (goalId: string) => ({
      url: `${API_BASE_URL}/savings-goals/${encodeURIComponent(goalId)}/pause`,
      method: 'PATCH'
    } as ApiEndpoint),
    RESUME: (goalId: string) => ({
      url: `${API_BASE_URL}/savings-goals/${encodeURIComponent(goalId)}/resume`,
      method: 'PATCH'
    } as ApiEndpoint)
  },

  // Financial assistant endpoints
  FINANCIAL_ASSISTANT: {
    CHAT: {
      url: `${API_BASE_URL}/financial-assistant/chat`,
      method: 'POST'
    } as ApiEndpoint
  }
};

/**
 * Common fetch configuration for API requests
 */
export const API_CONFIG = {
  headers: {
    'Content-Type': 'application/json'
  }
};

/**
 * Helper function to make API requests with consistent error handling and authentication
 * Returns rich response object with data, status, and headers
 */
export async function makeApiRequest<T = any>(
  endpoint: ApiEndpoint,
  data?: any,
  options: RequestInit = {},
  token: string | null = null
): Promise<ApiResult<T>> {
  const config: RequestInit = {
    ...API_CONFIG,
    method: endpoint.method,
    ...options,
    headers: {
      ...API_CONFIG.headers,
      ...(options.headers || {}),
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  };

  // Only add body if data is provided AND method supports it
  if (data !== undefined && ['POST', 'PUT', 'PATCH'].includes(endpoint.method)) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(endpoint.url, config);
    
    if (!response.ok) {
      let errorMessage = 'Request failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || JSON.stringify(errorData);
      } catch {
        errorMessage = await response.text();
      }
      
      throw new ApiError(
        errorMessage,
        response.status,
        endpoint.url,
        endpoint.method
      );
    }
    
    const responseData = await response.json();
    return {
      data: responseData,
      status: response.status,
      headers: response.headers
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof TypeError) {
      throw new ApiError(
        `Network error: ${error.message}`,
        undefined,
        endpoint.url,
        endpoint.method
      );
    }
    throw error;
  }
}

/**
 * Helper function to make authenticated API requests using access token (for API Gateway)
 */
export async function makeAuthenticatedApiRequest<T = any>(
  endpoint: ApiEndpoint,
  data: any = undefined,
  accessToken: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new Error('Access token is required and must be a string');
  }
  return makeApiRequest<T>(endpoint, data, options, accessToken);
}

